// ============================================================================
// UOM SERVICE
// ============================================================================
// common.tbl_uom via /api/v1/common/uoms: the read list every UoM picker uses
// (Service Master, Item Master packs) and the Units of Measure screen's writes
// (ADR-0058 slice 1).

import { get, post, put, del } from '../client/axiosClient';
import { uomEndpoints } from '../endpoints/uom.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, EmptyResponse } from '../types';
import type { UomDto, CreateUomRequest, UpdateUomRequest } from '../types/uom.types';

class UomService {
  /**
   * Fetch UOMs, optionally filtered by category (TIME | COUNT | WEIGHT |
   * VOLUME | LENGTH | AREA | OTHER) and/or a case-insensitive substring
   * search over code / name / symbol.
   */
  async getAll(category?: string, search?: string): Promise<UomDto[]> {
    const response = await get<ApiResponse<UomDto[]>>(uomEndpoints.list(category, search));
    return response.data.data ?? [];
  }

  async getById(id: string): Promise<UomDto> {
    const response = await get<ApiResponse<UomDto>>(uomEndpoints.getById(id));
    return response.data.data;
  }

  async create(data: CreateUomRequest): Promise<UomDto> {
    const response = await post<ApiResponse<UomDto>>(uomEndpoints.create(), data);
    return response.data.data;
  }

  async update(id: string, data: UpdateUomRequest): Promise<UomDto> {
    const response = await put<ApiResponse<UomDto>>(uomEndpoints.update(id), data);
    return response.data.data;
  }

  async delete(id: string, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(buildEndpointWithQuery(uomEndpoints.delete(id), { rowVersion }));
    return response.data;
  }

  async isCodeAvailable(code: string, excludeId?: string): Promise<boolean> {
    const endpoint = uomEndpoints.validateCode(code);
    const url = excludeId ? buildEndpointWithQuery(endpoint, { excludeId }) : endpoint;
    const response = await get<ApiResponse<boolean>>(url);
    return response.data.data;
  }
}

export const uomService = new UomService();
export default uomService;
